<template>
  <div>
    <BaseTable
      :items="list"
      :headers="tableHeaders"
      :loading="loading"
      :pagination="paginationConfig"
      :idPrefix="idPrefix"
      @page-change="pageChange"
    >
      <!-- Custom skeleton slot -->
      <template #skeleton>
        <td class="p-4">
          <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div class="h-4 bg-gray-200 rounded w-1/2"></div>
        </td>
        <td class="p-4">
          <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div class="h-4 bg-gray-200 rounded w-1/2"></div>
        </td>
        <td class="p-4">
          <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div class="h-4 bg-gray-200 rounded w-1/2"></div>
        </td>
        <td class="p-4">
          <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div class="h-4 bg-gray-200 rounded w-1/2"></div>
        </td>
        <td class="p-4">
          <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div class="h-4 bg-gray-200 rounded w-1/2"></div>
        </td>
        <td class="p-4">
          <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div class="h-4 bg-gray-200 rounded w-1/2"></div>
        </td>
        <td class="p-4 text-center flex space-x-4">
          <div class="h-9 bg-gray-200 rounded-full w-9 shadow"></div>
          <div class="h-9 bg-gray-200 rounded-full w-9 shadow"></div>
        </td>
      </template>

      <!-- Custom item slot -->
      <template #item="{ item }">
        <td class="p-4 text-sm text-gray-900">
          <div>
            {{ item.attributes.title["en-us"] }}
          </div>
        </td>
        <td class="p-4 text-sm text-gray-900">
          <div>
            {{ item.attributes.title["ar-sa"] }}
          </div>
        </td>
        <td class="p-4 text-sm text-gray-900">
          <div>
            {{ item.attributes.label }}
          </div>
        </td>
        <td class="p-4 text-sm text-gray-900">
          <div>
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="getTypeBadgeClass(item.attributes.type)">
              {{ getTypeLabel(item.attributes.type) }}
            </span>
          </div>
        </td>
        <td class="p-4 text-sm text-gray-900">
          <div>
            <!-- Link type: show clickable link -->
            <a v-if="item.attributes.type === 'link'"
               :href="item.attributes.url"
               target="_blank"
               rel="noopener noreferrer"
               class="text-blue-600 hover:text-blue-800 underline truncate block max-w-xs">
              {{ item.attributes.url }}
            </a>
            <!-- Page type: show content preview in small window -->
            <div v-else-if="item.attributes.type === 'page'"
                 class="max-w-xs max-h-20 overflow-hidden">
              <div class="text-xs text-gray-600 border border-gray-200 rounded p-2 bg-gray-50 max-h-16 overflow-y-auto"
                   v-html="getContentPreview(item.attributes.url)">
              </div>
            </div>
            <!-- Fallback for unknown types -->
            <span v-else class="text-gray-500">
              {{ item.attributes.url }}
            </span>
          </div>
        </td>
        <td class="p-4 text-sm text-gray-500">
          <div class="flex">
            <button
              :id="`${idPrefix}openeditpagemodal${item.id}`"
              @click="openEditPageModal(true, item.id)"
              v-if="item.attributes['is-editable']"
              class="text-blue-600 hover:text-white bg-blue-50 hover:bg-blue-600 rounded-full transition-colors flex items-center justify-center w-9 h-9 shadow-md mx-1 border-none"
              :title="translate('Edit')"
            >
              <i class="far fa-edit"></i>
            </button>
            <button
              :id="`${idPrefix}
              opendeletepagemodal${item.id}`"
              v-if="item.attributes['is-deletable']"
              @click="openDeletePage(item.id)"
              class="text-red-600 hover:text-white bg-red-50 hover:bg-red-600 rounded-full transition-colors flex items-center justify-center w-9 h-9 shadow-md mx-1 border-none"
              :title="translate('Delete')"
            >
              <i class="far fa-trash-alt"></i>
            </button>
          </div>
        </td>
      </template>

      <!-- Empty state slot -->
      <template #empty> No pages found </template>
    </BaseTable>
  </div>
</template>

<script>
import { translations } from "@/mixins";
import BaseTable from "@/components/BaseTable.vue";

export default {
  name: "PageList",

  components: {
    BaseTable,
  },

  mixins: [translations],

  props: {
    list: {
      type: Array,
      default: () => [],
    },
    listMeta: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoaded: false,
      total: 0,
      currentPage: 1,
      perPage: 10,
      idPrefix: "pages.",
      tableHeaders: [],
    };
  },

  created() {
    // Set table headers after component is created and mixins are applied
    this.tableHeaders = [
      {
        label: this.translate("Title") + " - English",
        id: "headertitleenglish",
      },
      {
        label: this.translate("Title") + " - Arabic",
        id: "headertitlearabic",
      },
      {
        label: this.translate("Page Name"),
        id: "headerpagename",
      },
      {
        label: this.translate("Type"),
        id: "headertype",
      },
      {
        label: this.translate("Content/URL"),
        id: "headercontent",
      },
      {
        label: this.translate("Action"),
        id: "headeraction",
      },
    ];
  },

  computed: {
    paginationConfig() {
      return {
        currentPage: this.currentPage,
        totalItems: this.total,
        perPage: this.perPage,
        enabled: this.isLoaded,
      };
    },
  },

  watch: {
    listMeta: {
      handler(newVal) {
        if (newVal) {
          this.isLoaded = true;
          this.total = newVal.total;
          this.currentPage = newVal.current_page;
          this.perPage = newVal.per_page;
        }
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    openEditPageModal(open, id) {
      this.$emit("openEditPageModal", open, id);
    },

    openDeletePage(id) {
      this.$emit("deletePage", id);
    },

    pageChange(page) {
      if (page !== this.currentPage) {
        this.$emit("getPages", page);
      }
    },

    getTypeLabel(type) {
      switch (type) {
        case 'link':
          return this.translate('Link');
        case 'page':
          return this.translate('Page');
        default:
          return type || this.translate('Unknown');
      }
    },

    getTypeBadgeClass(type) {
      switch (type) {
        case 'link':
          return 'bg-blue-100 text-blue-800';
        case 'page':
          return 'bg-green-100 text-green-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    },

    getContentPreview(content) {
      if (!content) return this.translate('No content');

      // Strip HTML tags and get plain text
      const plainText = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

      // Limit to 100 characters
      if (plainText.length <= 100) {
        return plainText;
      }

      return plainText.substring(0, 100) + '...';
    },
  },
};
</script>

<style scoped>
</style>
